import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Box, Button, CircularProgress, colors, DialogActions, DialogContent, Divider } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  compareEquipmentBranchClientSellRateImport,
  compareEquipmentBranchClientSellRateUpdateImport,
  downloadEquipmentSellRatesNewTemplate,
  downloadEquipmentSellRatesUpdateTemplate,
  hasExistingEquipmentSellRates,
} from 'api/equipmentApi';
import { handleMutationError } from 'util/handleMutationError';
import BulkUpdateClientBranchRatesForm from './BulkUpdateClientBranchRatesForm';
import { TabProps, comboLabel, useScopedRateTab, ScopedRateResultSteps } from './ScopedRateSharedTab';

const BranchClientSellRatesTab = ({ mode, onBusyChange, onClose }: TabProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    file,
    setFile,
    branch,
    setBranch,
    client,
    setClient,
    preview,
    setPreview,
    result,
    commitMutate,
    isCommitLoading,
    comboReady,
  } = useScopedRateTab('sell');

  const { mutate: previewMutate, isLoading: isPreviewLoading } = useMutation(
    mode === 'update'
      ? compareEquipmentBranchClientSellRateUpdateImport
      : compareEquipmentBranchClientSellRateImport,
    {
      onSuccess: (data) => setPreview(data),
      onError: (error) => {
        enqueueSnackbar(
          `Failed to read the file: ${handleMutationError(error)}`,
          { variant: 'error' }
        );
      },
    }
  );

  const { mutate: downloadMutate, isLoading: isDownloadingTemplate } = useMutation(
    mode === 'update'
      ? downloadEquipmentSellRatesUpdateTemplate
      : downloadEquipmentSellRatesNewTemplate,
    {
      onError: (error) => {
        enqueueSnackbar(
          `Failed to download the template: ${handleMutationError(error)}`,
          { variant: 'error' }
        );
      },
    }
  );

  const { data: hasExistingRates } = useQuery(
    ['equipment-sell-rates-exist', branch?.id, client?.id],
    () => hasExistingEquipmentSellRates({ branchId: branch?.id, clientId: client?.id }),
    { enabled: mode === 'update' && comboReady }
  );
  const noExistingDataMessage =
    mode === 'update' && comboReady && hasExistingRates === false
      ? `${comboLabel(branch, client)} doesn't have any existing sell rates yet. Use the "Import New" tab to add one first.`
      : null;

  const isBusy = isPreviewLoading || isCommitLoading || isDownloadingTemplate;
  React.useEffect(() => onBusyChange(isBusy), [isBusy, onBusyChange]);

  const step = result ? 'result' : preview ? 'preview' : 'form';
  const label = comboLabel(branch, client);

  return (
    <>
      {step === 'form' && (
        <>
          <DialogContent sx={{ p: 0 }}>
            <BulkUpdateClientBranchRatesForm
              file={file}
              setFile={setFile}
              isLoading={isPreviewLoading}
              rateKind="sell"
              mode={mode}
              branch={branch}
              setBranch={setBranch}
              client={client}
              setClient={setClient}
              onDownloadTemplate={() => {
                if (!comboReady) return;
                downloadMutate({
                  branchId: branch?.id,
                  clientId: client?.id,
                  branchName: branch?.name,
                  clientName: client?.legalName,
                });
              }}
              isDownloadingTemplate={isDownloadingTemplate}
              noExistingDataMessage={noExistingDataMessage}
            />
          </DialogContent>
          <Divider />
          <DialogActions>
            <Box mr={3} mt={1} mb={2}>
              <Button
                onClick={onClose}
                sx={{ color: colors.grey[600], mr: 1 }}
                disabled={isPreviewLoading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={isPreviewLoading || !file || !comboReady}
                endIcon={
                  isPreviewLoading ? (
                    <CircularProgress size={15} />
                  ) : (
                    <ArrowForwardIcon />
                  )
                }
                onClick={() => {
                  if (!file || !comboReady) return;
                  previewMutate({
                    file,
                    branchId: branch?.id,
                    clientId: client?.id,
                  });
                }}>
                {isPreviewLoading ? 'Please wait...' : 'Preview'}
              </Button>
            </Box>
          </DialogActions>
        </>
      )}

      {(step === 'preview' || step === 'result') && preview && (
        <ScopedRateResultSteps
          preview={preview}
          result={result}
          isCommitLoading={isCommitLoading}
          comboLabel={label}
          mode={mode}
          branch={branch}
          client={client}
          onBack={() => setPreview(null)}
          onConfirm={() => {
            if (!preview.previewToken) return;
            commitMutate({ previewToken: preview.previewToken });
          }}
          onClose={onClose}
        />
      )}
    </>
  );
};

export default BranchClientSellRatesTab;
